'use client';
import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';

import Breadcrumb from '@components/Breadcrumb/Breadcrumb';
import { FlexibleDiv } from '@components/formStyles';
import { callService } from '@api/remote-call';
import ScrollToTop from '@components/ScrollToTop/ScrollToTop';
import { BreadCrumbListSchemaOrg } from '@utils/schemaorg/BreadCrumbListSchemaOrg';
import { useDevice } from '@contexts/device';

import {
  Layout,
  ContainerItem,
  CampaignPromotionsWrapper,
  RulesInformationContainer,
  SearchContainer,
  ContentContainer,
  CityMapWrapper,
  CityMapLabel,
  MapContent,
  ResponsiveTitle,
  Container,
} from './styled';
import { formatToCampaignsBannerData } from '../../homepage/formats';
import { LandingBannerContainer } from '../../homepage/HomeStyle';
import SearchBar from './SearchBar';

const ItemInfo = dynamic(() => import('./ItemInfo'));
const Map = dynamic(() => import('@components/ui/map/Map'));
const HeroCampaignBanner = dynamic(
  () => import('@components/ui/banners/hero-campaign-banner/HeroCampaignBanner')
);
const DescriptionComponent = dynamic(() => import('./DescriptionComponent'));
const OnlineOperationsDesktop = dynamic(
  () => import('./OnlineOperationsDesktop')
);
const OnlineOperationsMobile = dynamic(
  () => import('./OnlineOperationsMobile')
);

const PopularCitiesContainer = dynamic(
  () => import('./PopularCitiesContainer')
);
const RulesInformation = dynamic(
  () => import('../../landing/components/rules-information/RulesInformation')
);
const CampaignPromotions = dynamic(() =>
  import(
    '../../landing/components/campaign/CampaignPromotions/CampaignPromotions'
  ).then((module) => module.CampaignPromotions)
);

export const breadCrumbItems = [
  { title: 'Ana Sayfa', url: '/' },
  { title: 'Uçak', url: '' },
];

const FlightTicket = ({ flightsContent }: { flightsContent?: any }) => {
  const { isDesktop } = useDevice();

  const { data } = useQuery(
    ['flight-landing'],
    async () => {
      return callService(
        {
          service: 'ContentsService',
          method: 'getFlightLanding',
          version: 'v3',
        },
        {
          slug: 'flight-landing',
        }
      );
    },
    { initialData: flightsContent, staleTime: Infinity }
  );

  const showMap =
    data?.informations?.[0]?.coordinates?.[0]?.lattitude &&
    data?.informations?.[0]?.coordinates?.[0]?.longtitude;

  return (
    <Container>
      <BreadCrumbListSchemaOrg data={breadCrumbItems} />
      <ScrollToTop />
      <Layout>
        <SearchContainer>
          {data?.searchBar ? (
            <ContainerItem marginTop={isDesktop ? 40 : 20}>
              <SearchBar
                isFlightTicketPage={true}
                showIsCheckbox={true}
                data={data?.searchBar?.[0]}
                isHoliday={false}
              />
            </ContainerItem>
          ) : null}
          <ContainerItem marginTop={20}>
            <ContentContainer>
              <Breadcrumb items={breadCrumbItems} />
              {isDesktop ? null : (
                <ResponsiveTitle>{data?.searchBar?.[0]?.name}</ResponsiveTitle>
              )}
            </ContentContainer>
          </ContainerItem>
        </SearchContainer>
        <ContentContainer>
          {isDesktop ? <OnlineOperationsDesktop /> : <OnlineOperationsMobile />}

          <ContainerItem marginTop={30}>
            {data?.description1 ? (
              <DescriptionComponent description={data.description1} />
            ) : null}
            {data?.listings?.length < 0 ? (
              <FlexibleDiv top={60} bottom={40}>
                <PopularCitiesContainer data={data.listings} />
              </FlexibleDiv>
            ) : null}
          </ContainerItem>

          {showMap ? (
            <MapContent>
              <CityMapLabel>
                {data?.informations?.[0]?.title || ''}
              </CityMapLabel>
              <CityMapWrapper>
                <Map
                  latitude={
                    data?.informations?.[0]?.coordinates?.[0]?.lattitude
                  }
                  longitude={
                    data?.informations?.[0]?.coordinates?.[0]?.longtitude
                  }
                  zoom={6}
                  height='254px'
                />
              </CityMapWrapper>
            </MapContent>
          ) : null}

          {data?.description2 ? (
            <ContainerItem marginTop={40}>
              <DescriptionComponent description={data.description2} />
              {isDesktop ? <FlexibleDiv bottom={20} /> : null}
            </ContainerItem>
          ) : null}

          <ContainerItem>
            {data?.description3 ? (
              <ContainerItem marginTop={20}>
                <DescriptionComponent description={data.description3} />
              </ContainerItem>
            ) : null}
            {data?.information1?.length ? (
              <RulesInformationContainer>
                <RulesInformation
                  header={data?.information1?.[0]?.title}
                  text={data?.information1?.[0]?.text}
                  buttonText={data?.information1?.[0]?.boldLink?.[0]?.name}
                />
              </RulesInformationContainer>
            ) : null}
          </ContainerItem>
          {data?.description4 ? (
            <ContainerItem marginTop={40}>
              <DescriptionComponent description={data.description4} />
            </ContainerItem>
          ) : null}
          <ContainerItem>
            <CampaignPromotionsWrapper>
              <ItemInfo
                title={data?.campaignPromotion?.[0]?.title || ''}
                description={data?.campaignPromotion?.[0]?.text || ''}
              />
              {data?.campaignPromotion &&
              data?.campaignPromotion?.length > 0 ? (
                <CampaignPromotions
                  data={data.campaignPromotion?.[0]?.items}
                  link={data.campaignPromotion?.[0]?.link?.[0]}
                  showHeader={false}
                  linkPosition='flex-start'
                  imagesKey='homePageImages'
                />
              ) : null}
            </CampaignPromotionsWrapper>
          </ContainerItem>
          {data?.description5 ? (
            <ContainerItem marginTop={40}>
              <DescriptionComponent description={data.description5} />
            </ContainerItem>
          ) : null}
          {data?.campaignBanner && data?.campaignBanner?.length > 0 ? (
            <ContainerItem>
              <LandingBannerContainer>
                <HeroCampaignBanner
                  data={formatToCampaignsBannerData(
                    data.campaignBanner?.[0],
                    isDesktop
                  )}
                />
              </LandingBannerContainer>
            </ContainerItem>
          ) : null}
        </ContentContainer>
      </Layout>
      <FlexibleDiv bottom={20} />
    </Container>
  );
};

export default FlightTicket;
